import { motion } from "framer-motion";
import { useContent } from "@/lib/content";

export const IMG_FALLBACK =
  "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const onImgError = (e) => {
  if (e.currentTarget.dataset.fb) return;
  e.currentTarget.dataset.fb = "1";
  e.currentTarget.src = IMG_FALLBACK;
};

const IMGS = {
  faucet:
    "https://images.unsplash.com/photo-1647664856968-880b8eccd588?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwY2xvdGhpbmclMjByYWNrfGVufDB8fHx8MTc4Njk1ODk0Nnww&ixlib=rb-4.1.0&q=85",
  originals:
    "https://images.unsplash.com/photo-1559697242-a465f2578a95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwZWRpdG9yaWFsJTIwZmFzaGlvbiUyMGdydW5neXxlbnwwfHx8fDE3ODY5NTkwOTl8MA&ixlib=rb-4.1.0&q=85",
  skate:
    "https://images.unsplash.com/photo-1649281610716-94e4ee026d91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxza2F0ZWJvYXJkaW5nJTIwdW5kZXJncm91bmQlMjBibGFjayUyMHdoaXRlfGVufDB8fHx8MTc4Njk1OTA5OXww&ixlib=rb-4.1.0&q=85",
  goods:
    "https://images.pexels.com/photos/10132110/pexels-photo-10132110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const cardIn = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

function Sticker({ label, color }) {
  return (
    <span
      className="absolute top-3 right-3 z-20 font-mono-ui text-xs font-600 uppercase px-3 py-1 border-2 border-[#090909] rotate-[-5deg] shadow-[2px_2px_0_#090909]"
      style={{ background: color }}
    >
      {label}
    </span>
  );
}

function Teaser({ id, index, kicker, title, blurb, img, sticker, stickerColor, span }) {
  const go = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <motion.button
      custom={index}
      variants={cardIn}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      onClick={go}
      data-testid={`teaser-${id}`}
      className={`group relative text-left border-2 border-[#090909] bg-[#F2EFEB] text-[#090909] overflow-hidden flex flex-col ${span}`}
    >
      <Sticker label={sticker} color={stickerColor} />
      <div className="relative overflow-hidden flex flex-col flex-1 min-h-0">
        <img
          src={img}
          alt={title}
          onError={onImgError}
          className="w-full h-[240px] md:h-auto md:flex-1 md:min-h-0 object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
        />
      </div>
      <div className="border-t-2 border-[#090909] p-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <span className="overline text-[#FF2A00]">{kicker}</span>
          <h3 className="font-display text-3xl md:text-4xl leading-none mt-1 break-words">{title}</h3>
          <p className="font-mono-ui text-xs mt-2 opacity-70 max-w-xs">{blurb}</p>
        </div>
        <span className="font-display text-3xl shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform">
          ↗
        </span>
      </div>
    </motion.button>
  );
}

const ALL = ["originals", "faucet", "skate", "goods"];
const TEASER_META = {
  originals: { kicker: "In-House Line", blurb: "The house cut. Made in-house, worn worldwide — our flagship line.", sticker: "DROP 01" },
  faucet: { kicker: "Curated / Vintage / Preowned", blurb: "Hand-picked non-in-house apparel, rare vintage and preowned grails.", sticker: "CURATED" },
  skate: { kicker: "Boards / Culture", blurb: "Decks, hardware and the culture that carved it.", sticker: "SESSION" },
  goods: { kicker: "Everything Else", blurb: "Objects, print, accessories. The odds, ends and artifacts.", sticker: "MISC" },
};

export default function Collections() {
  const { t } = useContent();
  const heading = t("collections_heading").split(" ");
  const order = t("collection_order").split(",").map((s) => s.trim()).filter((c) => ALL.includes(c));
  const fullOrder = [...order, ...ALL.filter((c) => !order.includes(c))];

  // First item = large feature card; last item = full-width; middle = half cards.
  const spanFor = (i) => {
    if (i === 0) return "md:col-span-8 md:row-span-2";
    if (i === fullOrder.length - 1) return "md:col-span-12";
    return "md:col-span-4";
  };

  return (
    <section
      data-testid="collections-teasers"
      className="bg-[#090909] text-[#F2EFEB] px-4 md:px-8 py-16 md:py-32"
    >
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <h2 className="font-display text-6xl md:text-8xl leading-[0.85] break-words">
          {heading[0]}<br />{heading.slice(1).join(" ")}
        </h2>
        <p className="font-mono-ui text-sm max-w-xs opacity-70">
          {t("collections_blurb")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:auto-rows-[280px]">
        {fullOrder.map((id, i) => {
          const bento = (t(`bento_${id}`) || "").trim();
          return (
            <Teaser
              key={id}
              id={id}
              index={i}
              kicker={TEASER_META[id].kicker}
              title={t(`col_${id}_title`)}
              blurb={TEASER_META[id].blurb}
              img={bento || IMGS[id] || IMG_FALLBACK}
              sticker={TEASER_META[id].sticker}
              stickerColor={i % 2 === 0 ? "#DFFF00" : "#FF2A00"}
              span={spanFor(i)}
            />
          );
        })}
      </div>
    </section>
  );
}
